import {
  deletePartnerLink,
  getPartnerAccount,
  getPartnerAccountTransactions,
  getPartnerClients,
  getPartnerClientsExtended,
  getPartnerClientsStats,
  getPartnerLinks,
  getPartnerPayments,
  getPartnerPayouts,
  getPartnerPayoutsStats,
  getPartnerRules,
  getPartnerSummary,
  IPartnerAccountDto,
  IPartnerAccountTransactionsResponseDto,
  IPartnerClientDto,
  IPartnerClientsExtendedResponseDto,
  IPartnerClientsRequestDto,
  IPartnerClientsStatsDto,
  IPartnerLinkDto,
  IPartnerPaymentsRequestDto,
  IPartnerPaymentsResponseDto,
  IPartnerPayoutsResponseDto,
  IPartnerPayoutsStatsDto,
  IPartnerRulesDto,
  IPartnerSummaryDto,
  IPartnerSummaryRequestDto,
  patchPartnerLink,
  postPartnerAccountTopup,
  postPartnerClient,
  postPartnerClientExtended,
  postPartnerLink,
} from '@/shared/api/partner';

const PARTNER_QUERY_KEY = {
  BASE: 'partner',
  SUMMARY: 'summary',
  PAYMENTS: 'payments',
  CLIENTS: 'clients',
} as const;

/** @deprecated Use `partnerAccountQuery` + `partnerPayoutsStatsQuery`. */
export const partnerSummaryQuery = (params?: IPartnerSummaryRequestDto) => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, PARTNER_QUERY_KEY.SUMMARY, params],
  queryFn: (): Promise<IPartnerSummaryDto> => getPartnerSummary(params),
});

/** @deprecated Use `partnerPayoutsQuery`. */
export const partnerPaymentsQuery = (params?: IPartnerPaymentsRequestDto) => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, PARTNER_QUERY_KEY.PAYMENTS, params],
  queryFn: (): Promise<IPartnerPaymentsResponseDto> =>
    getPartnerPayments(params),
});

/** @deprecated Use `partnerClientsExtendedQuery`. */
export const partnerClientsQuery = (params?: IPartnerClientsRequestDto) => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, PARTNER_QUERY_KEY.CLIENTS, params],
  queryFn: (): Promise<IPartnerClientDto[]> => getPartnerClients(params),
});

export const partnerLinksQuery = () => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, 'links'] as const,
  queryFn: (): Promise<IPartnerLinkDto[]> => getPartnerLinks(),
});

export const partnerLinkCreate = () => ({
  mutationFn: (data: { name: string }) => postPartnerLink(data),
});

export const partnerLinkPatch = () => ({
  mutationFn: (input: { id: string; data: { name?: string; isActive?: boolean } }) =>
    patchPartnerLink(input.id, input.data),
});

export const partnerLinkDelete = () => ({
  mutationFn: (id: string) => deletePartnerLink(id),
});

/** @deprecated Use `partnerClientCreateExtended`. */
export const partnerClientCreate = () => ({
  mutationFn: (data: { email: string; name: string }) => postPartnerClient(data),
});

// ---------------------------------------------------------------------------
// New partner query/mutation hooks (account / transactions / clients-extended /
// payouts / rules)
// ---------------------------------------------------------------------------

export const partnerAccountQuery = () => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, 'account'] as const,
  queryFn: (): Promise<IPartnerAccountDto> => getPartnerAccount(),
});

export const partnerAccountTransactionsQuery = (params: {
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}) => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, 'account', 'transactions', params] as const,
  queryFn: (): Promise<IPartnerAccountTransactionsResponseDto> =>
    getPartnerAccountTransactions(params),
});

export const partnerAccountTopupMutate = () => ({
  mutationFn: (data: {
    amountMinor: number;
    method: 'sbp' | 'card' | 'invoice';
  }) => postPartnerAccountTopup(data),
});

export const partnerClientsExtendedQuery = (params: {
  search?: string;
  showInactive?: boolean;
}) => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, 'clients-extended', params] as const,
  queryFn: (): Promise<IPartnerClientsExtendedResponseDto> =>
    getPartnerClientsExtended(params),
});

export const partnerClientsStatsQuery = () => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, 'clients-stats'] as const,
  queryFn: (): Promise<IPartnerClientsStatsDto> => getPartnerClientsStats(),
});

export const partnerClientCreateExtended = () => ({
  mutationFn: (data: { email: string; phone: string; name: string }) =>
    postPartnerClientExtended(data),
});

export const partnerPayoutsQuery = (params: {
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}) => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, 'payouts', params] as const,
  queryFn: (): Promise<IPartnerPayoutsResponseDto> => getPartnerPayouts(params),
});

export const partnerPayoutsStatsQuery = (params: {
  from?: string;
  to?: string;
}) => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, 'payouts-stats', params] as const,
  queryFn: (): Promise<IPartnerPayoutsStatsDto> => getPartnerPayoutsStats(params),
});

export const partnerRulesQuery = () => ({
  queryKey: [PARTNER_QUERY_KEY.BASE, 'rules'] as const,
  queryFn: (): Promise<IPartnerRulesDto> => getPartnerRules(),
  staleTime: 1000 * 60 * 60,
});
